import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Users, Award } from "lucide-react";

export default function ReferralModal({ open, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open) api.get("/referrals").then((r) => setData(r.data)).catch(() => {});
  }, [open]);

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161f] border-white/10 sm:max-w-md" data-testid="referral-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl uppercase tracking-tight flex items-center gap-2">
            <Gift className="w-6 h-6 cyan-text" /> Refer & <span className="cyan-text">Earn</span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share your link. When a friend upgrades to Pro, you get a free month.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Input value={link} readOnly className="bg-[#0b0c10] border-white/10 font-tab text-sm" data-testid="referral-link-input" />
            <Button onClick={copy} className="bg-[#00f0ff] text-black hover:bg-[#33f4ff] shrink-0" data-testid="referral-copy-button">
              <Copy className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-slate-400" />
              <div className="font-heading text-3xl" data-testid="referral-count">{data?.referred_count ?? 0}</div>
              <div className="text-xs text-slate-500 font-tab">Friends joined</div>
            </div>
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
              <Award className="w-5 h-5 mx-auto mb-1 amber-text" />
              <div className="font-heading text-3xl amber-text" data-testid="reward-count">{data?.rewarded_count ?? 0}</div>
              <div className="text-xs text-slate-500 font-tab">Free months earned</div>
            </div>
          </div>

          <div className="rounded-xl border border-[#ffab00]/30 bg-[#ffab00]/5 p-4" data-testid="milestone-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-300 font-semibold">Refer 5 paying friends → free Lifetime</span>
              <span className="font-tab text-sm amber-text">{Math.min(data?.rewarded_count ?? 0, 5)} / 5</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#ffab00]" style={{ width: `${Math.min(((data?.rewarded_count ?? 0) / 5) * 100, 100)}%` }} />
            </div>
            {(data?.rewarded_count ?? 0) >= 5 && (
              <p className="text-xs cyan-text mt-2 font-semibold">Milestone hit — Lifetime unlocked!</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
